import React, { useState, useEffect, useCallback } from 'react';
import { FaBell, FaCheck, FaTrash, FaCalendarAlt, FaCommentDots, FaVideo, FaFlask, FaCreditCard } from 'react-icons/fa';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import { useApp } from '../context/AppContext';
import { getNotifications, markNotificationRead, markAllNotificationsRead, deleteNotification } from '../services/api';

const typeIcon = { appointment: FaCalendarAlt, message: FaCommentDots, telemedicine: FaVideo, lab: FaFlask, billing: FaCreditCard, general: FaBell };
const typeColor = { appointment: '#1976d2', message: '#E8500A', telemedicine: '#7c3aed', lab: '#2e7d32', billing: '#e65100', general: '#8888a8' };

const Notifications = () => {
  const { showToast, updateBadges } = useApp();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getNotifications();
      setNotifications(res.data?.data || []);
      updateBadges({ notifications: res.data?.unread || 0 });
    } catch { setNotifications([]); }
    setLoading(false);
  }, [updateBadges]);

  useEffect(() => { fetch(); }, [fetch]);

  const handleRead = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n));
      updateBadges({ notifications: notifications.filter(n => !n.isRead && n.id !== id).length });
    } catch {}
  };

  const handleReadAll = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      updateBadges({ notifications: 0 });
      showToast('All marked as read', 'success');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteNotification(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      showToast('Deleted');
    } catch (err) { showToast(err.message || 'Error', 'error'); }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;

  return (
    <div className="page-wrapper">
      <Header title="Notifications" />
      <Sidebar />
      <main className="page-content">
        <div className="page-header">
          <div>
            <h1><FaBell style={{ color: 'var(--primary)', marginRight: 8 }} />Notifications</h1>
            <p>{unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}</p>
          </div>
          {unreadCount > 0 && (
            <button className="btn btn-outline" style={{ fontSize: 13 }} onClick={handleReadAll}>
              <FaCheck /> Mark All Read
            </button>
          )}
        </div>

        {loading ? (
          <div className="loading-container"><div className="spinner" /></div>
        ) : notifications.length === 0 ? (
          <div className="empty-state">
            <FaBell className="empty-state-icon" />
            <h3>No notifications</h3>
            <p>You're all caught up!</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {notifications.map(notif => {
              const Icon = typeIcon[notif.type] || FaBell;
              const color = typeColor[notif.type] || '#8888a8';
              return (
                <div key={notif.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'flex-start', gap: 12, opacity: notif.isRead ? 0.7 : 1, borderLeft: notif.isRead ? '3px solid transparent' : '3px solid var(--primary)' }}>
                  <div style={{ width: 40, height: 40, borderRadius: '50%', background: color + '20', display: 'flex', alignItems: 'center', justifyContent: 'center', color, flexShrink: 0 }}>
                    <Icon />
                  </div>
                  <div style={{ flex: 1 }}>
                    {notif.title && <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 2 }}>{notif.title}</div>}
                    <div style={{ fontSize: 13, color: 'var(--text-medium)', lineHeight: 1.5 }}>{notif.message}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-light)', marginTop: 4 }}>
                      {notif.createdAt ? new Date(notif.createdAt).toLocaleString('en-IN') : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    {!notif.isRead && (
                      <button className="action-btn" style={{ background: '#e8f5e9', color: '#2e7d32' }} onClick={() => handleRead(notif.id)} title="Mark as read"><FaCheck /></button>
                    )}
                    <button className="action-btn action-btn-delete" onClick={() => handleDelete(notif.id)} title="Delete"><FaTrash /></button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
      <BottomNav />
    </div>
  );
};

export default Notifications;
